import React, { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { AuthProvider } from '../contexts/AuthContext';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import GlobalErrorBoundary from '../components/shared/GlobalErrorBoundary';
import i18next from 'i18next';
import '../i18n';

function RTLAwareToaster() {
  const { isRTL } = useLanguage();
  return <Toaster position={isRTL ? 'top-left' : 'top-right'} richColors />;
}

export default function AppProviders({ children }) {
  const [queryClient] = useState(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (error?.status === 401) return; // handled by httpClient
        // Assessment-scoped queries can transiently 404 when the parent
        // assessment is deleted but child components are still mounted with
        // the stale id. Those queries opt out via meta.silentNotFound so the
        // user doesn't see "Assessment 75 not found" after a successful delete.
        if (error?.status === 404 && query?.meta?.silentNotFound) return;
        const message = error?.message || i18next.t('common:errorOccurred');
        toast.error(message);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }));
  return (
    <GlobalErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LanguageProvider>
              <RTLAwareToaster />
              {children}
            </LanguageProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </GlobalErrorBoundary>
  );
}
